'use client';

import { Building2, MapPin } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import React from 'react';

import { Button } from '@ui/components/common/button';
import { ImageWithFallback } from '@ui/components/common/imageWithFallback/ImageWithFallback';
import type { SearchHub } from '@website/services/search.service';

import { DrawerGroupNavigation } from './DrawerGroupNavigation';

interface HubDrawerContentProps {
  hub: SearchHub;
  /** The activities found at the Hub, which the arrows go on to. */
  activityCount: number;
  onPrev?: () => void;
  onNext?: () => void;
  currentIndex?: number;
  totalCount?: number;
}

/** A Hub's pin tapped on a phone: the Hub first, its activities one arrow away, as a grouped pin. */
export function HubDrawerContent({
  hub,
  activityCount,
  onPrev,
  onNext,
  currentIndex,
  totalCount
}: HubDrawerContentProps) {
  const t = useTranslations('HubResult');
  const router = useRouter();
  const locale = useLocale();
  const href = `/${locale}/hub/${hub.id}`;
  const viewHub = () => router.push(href);

  return (
    <div className="flex flex-col h-full">
      <DrawerGroupNavigation onPrev={onPrev} onNext={onNext} currentIndex={currentIndex} totalCount={totalCount} />
      <button
        type="button"
        onClick={viewHub}
        className="relative mt-3 block h-[250px] w-full overflow-hidden rounded-xl text-left text-white"
      >
        <span className="absolute inset-0">
          <ImageWithFallback
            src={hub.coverImageUrl ?? undefined}
            alt=""
            className="h-full w-full object-cover"
            sizes="100vw"
            fallback={
              <span className="flex h-full w-full items-center justify-center bg-primary-700">
                <Building2 className="size-12 text-white/70" strokeWidth={1.5} aria-hidden />
              </span>
            }
          />
          <span className="absolute inset-0 bg-gradient-to-b from-black/10 to-black/70" />
        </span>
        <span className="relative flex h-full flex-col justify-between p-4">
          <span className="inline-flex items-center gap-1 self-start rounded-md bg-white/90 px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-primary-700">
            <Building2 className="size-3" aria-hidden />
            {t('kind')}
          </span>
          <span className="flex flex-col gap-1">
            <span className="text-2xl font-bold">{hub.name}</span>
            {activityCount > 0 && <span className="text-base">{t('activitiesHere', { count: activityCount })}</span>}
            <span className="flex items-center gap-1 text-sm text-white/90">
              <MapPin className="size-4 shrink-0" aria-hidden />
              <span className="truncate">
                {hub.street}, {hub.locality}
              </span>
            </span>
          </span>
        </span>
      </button>
      <div
        className="pt-4 mt-auto bg-white border-t border-neutral-200 flex"
        style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
      >
        <Button variant="primary" label={t('viewHub')} onClick={viewHub} className="flex-1" />
      </div>
    </div>
  );
}
